import { memo, type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { getFieldErrorId } from "./UseRequiredFieldError";

interface CampaignFormFieldProps {
  label: string;
  children: ReactNode;
  htmlFor?: string;
  labelId?: string;
  required?: boolean;
  hint?: string;
  hasError?: boolean;
  errorMessage?: string;
  counter?: ReactNode;
  helperText?: string;
  badge?: ReactNode;
}

const CampaignFormField: FC<CampaignFormFieldProps> = ({
  label,
  children,
  htmlFor,
  labelId,
  required = false,
  hint,
  hasError = false,
  errorMessage,
  counter,
  helperText,
  badge,
}) => {
  const { t } = useTranslation();

  const labelElement = (
    <label id={labelId} htmlFor={htmlFor} className="campaign-flow-field__label">
      {label}
      {required && (
        <span className="campaign-flow-field__required" aria-hidden>
          *
        </span>
      )}
      {hint && <span className="campaign-flow-field__hint">{hint}</span>}
      {badge}
    </label>
  );

  const errorElement = hasError ? (
    <span
      id={htmlFor ? getFieldErrorId(htmlFor) : undefined}
      role="alert"
      className="campaign-flow-field__error"
    >
      {errorMessage ?? t("campaignFlow.common.fieldRequired")}
    </span>
  ) : null;

  return (
    <div className={`campaign-flow-field ${hasError ? "campaign-flow-field--error" : ""}`}>
      {labelElement}
      {children}
      {helperText && <span className="campaign-flow-field__helper">{helperText}</span>}
      {counter ? (
        <div className="campaign-flow-field__meta">
          {errorElement}
          {counter}
        </div>
      ) : (
        errorElement
      )}
    </div>
  );
};

export default memo(CampaignFormField);
